import React, { useCallback, useState } from 'react';
import { TableRowHtmlProps } from './TableRow';
import { BaseTableRow } from './types';

interface UseMovableTableRowsParams<RowEntity extends BaseTableRow> {
  isRowDraggable: (row: RowEntity) => boolean;
  isRowDropTarget: (row: RowEntity, draggedRowId: string) => boolean;
  onMove: (draggedRowId: string, dropTargetId: string | null) => void;
}

export interface DropZoneProps {
  isActive: boolean;
  onDragEnter: (event: React.DragEvent) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
}

function isPointerStillInsideTarget(event: React.DragEvent): boolean {
  const { relatedTarget, currentTarget, clientX, clientY } = event;
  if (relatedTarget instanceof Node && currentTarget.contains(relatedTarget)) {
    return true;
  }
  // relatedTarget is often null for dragleave; fall back to hit-testing.
  const el = document.elementFromPoint(clientX, clientY);
  return el != null && currentTarget.contains(el);
}

function buildDraggableRowProps({
  rowId,
  draggedRowId,
  endDrag,
  setDraggedRowId,
}: {
  rowId: string;
  draggedRowId: string | null;
  endDrag: () => void;
  setDraggedRowId: (id: string | null) => void;
}): TableRowHtmlProps {
  return {
    draggable: true,
    style: {
      cursor: draggedRowId === rowId ? 'grabbing' : 'grab',
    },
    ...(draggedRowId === rowId ? { 'data-dragging': 'true' as const } : {}),
    onDragStart: (event) => {
      const { dataTransfer } = event;
      dataTransfer.effectAllowed = 'move';
      // Firefox only starts a native drag when data is set.
      dataTransfer.setData('text/plain', rowId);
      // Defer marking the drag active: setting it synchronously re-renders and
      // mutates the DOM (e.g. inserting a drop zone) during dragstart, which
      // Chrome treats as a cancel.
      setTimeout(() => setDraggedRowId(rowId), 0);
    },
    onDragEnd: endDrag,
  };
}

function buildDropTargetRowProps({
  rowId,
  hoveredRowId,
  setHoveredRowId,
  onDropTarget,
}: {
  rowId: string;
  hoveredRowId: string | null;
  setHoveredRowId: React.Dispatch<React.SetStateAction<string | null>>;
  onDropTarget: (targetId: string) => void;
}): TableRowHtmlProps {
  return {
    $isDropTarget: hoveredRowId === rowId,
    onDragEnter: (event) => {
      event.preventDefault();
      setHoveredRowId(rowId);
    },
    onDragOver: (event) => {
      event.preventDefault();
      const { dataTransfer } = event;
      dataTransfer.dropEffect = 'move';
    },
    onDragLeave: (event) => {
      // Ignore leave events from crossing into nested children — those were
      // clearing the drop highlight.
      if (isPointerStillInsideTarget(event)) return;
      setHoveredRowId((prev) => (prev === rowId ? null : prev));
    },
    onDrop: (event) => {
      event.preventDefault();
      onDropTarget(rowId);
    },
  };
}

function buildDropZoneProps({
  targetId,
  hoveredDropZoneTargetId,
  setHoveredDropZoneTargetId,
  onDropTarget,
}: {
  targetId: string | null;
  hoveredDropZoneTargetId: string | null | undefined;
  setHoveredDropZoneTargetId: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  onDropTarget: (targetId: string | null) => void;
}): DropZoneProps {
  return {
    isActive: hoveredDropZoneTargetId === targetId,
    onDragEnter: (event) => {
      event.preventDefault();
      setHoveredDropZoneTargetId(targetId);
    },
    onDragOver: (event) => {
      event.preventDefault();
      const { dataTransfer } = event;
      dataTransfer.dropEffect = 'move';
    },
    onDragLeave: (event) => {
      if (isPointerStillInsideTarget(event)) return;
      setHoveredDropZoneTargetId((prev) =>
        prev === targetId ? undefined : prev,
      );
    },
    onDrop: (event) => {
      event.preventDefault();
      onDropTarget(targetId);
    },
  };
}

export function useMovableTableRows<RowEntity extends BaseTableRow>({
  isRowDraggable,
  isRowDropTarget,
  onMove,
}: UseMovableTableRowsParams<RowEntity>) {
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  // undefined = no drop zone hovered; null is a valid targetId (e.g. top-level).
  const [hoveredDropZoneTargetId, setHoveredDropZoneTargetId] = useState<
    string | null | undefined
  >(undefined);

  const endDrag = useCallback(() => {
    setDraggedRowId(null);
    setHoveredRowId(null);
    setHoveredDropZoneTargetId(undefined);
  }, []);

  const handleDrop = useCallback(
    (dropTargetId: string | null) => {
      if (draggedRowId) onMove(draggedRowId, dropTargetId);
      endDrag();
    },
    [draggedRowId, endDrag, onMove],
  );

  const getMovableRowProps = useCallback(
    (row: RowEntity): TableRowHtmlProps => ({
      ...(isRowDraggable(row)
        ? buildDraggableRowProps({
            rowId: row.id,
            draggedRowId,
            endDrag,
            setDraggedRowId,
          })
        : {}),
      ...(draggedRowId &&
      draggedRowId !== row.id &&
      isRowDropTarget(row, draggedRowId)
        ? buildDropTargetRowProps({
            rowId: row.id,
            hoveredRowId,
            setHoveredRowId,
            onDropTarget: handleDrop,
          })
        : {}),
    }),
    [
      draggedRowId,
      endDrag,
      handleDrop,
      hoveredRowId,
      isRowDraggable,
      isRowDropTarget,
    ],
  );

  const getDropZoneProps = useCallback(
    (targetId: string | null): DropZoneProps =>
      buildDropZoneProps({
        targetId,
        hoveredDropZoneTargetId,
        setHoveredDropZoneTargetId,
        onDropTarget: handleDrop,
      }),
    [handleDrop, hoveredDropZoneTargetId],
  );

  return {
    getMovableRowProps,
    getDropZoneProps,
    draggedRowId,
    isDragging: draggedRowId != null,
  };
}
